import {
  AfterViewInit,
  Component,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  SimpleChanges,
} from '@angular/core';

import { isPlatformBrowser, NgStyle } from '@angular/common';
import { RouterLink } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { TranslatePipe } from '@ngx-translate/core';
import { MainSlide, FALLBACK_SLIDES } from './main-slider.model';
import { LocaleService } from '../../../../Shared/services/locale/locale.service';
import { HomepageSlidesService, HomepageSlide } from '../../../../Shared/_services/homepage-slides/homepage-slides.service';

// Declare Swiper as a global variable (loaded via script tag)
declare const Swiper: any;

@Component({
  selector: 'app-main-slider',
  standalone: true,
  templateUrl: './main-new-slider.component.html',
  styleUrls: ['./main-new-slider.component.scss'],
  imports: [NgStyle, RouterLink, TranslatePipe],
})
export class MainSliderComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  isBrowser: boolean;
  @Input() translations: any;
  @Input() initialSlides: HomepageSlide[] | null = null;
  @Input() disableAutoFetch = false;

  slides: MainSlide[] = [];
  hasMultipleSlides = false;
  isLoading = true;
  isApiLoaded = false;
  private lastLoadedLocale: string | null = null;

  // Store Swiper instance for cleanup
  private swiperInstance: any = null;
  private destroy$ = new Subject<void>();

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private localeService: LocaleService,
    private slidesService: HomepageSlidesService
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    const loadedFromInput = this.applyInitialSlides();
    if (!loadedFromInput && !this.disableAutoFetch) {
      this.loadSlides();
    } else if (!loadedFromInput && this.disableAutoFetch) {
      this.isLoading = false;
      this.slides = [];
      this.hasMultipleSlides = false;
    }

    // React to language changes
    this.localeService.languageCode$
      .pipe(takeUntil(this.destroy$))
      .subscribe((lang) => {
        if (!this.isApiLoaded || !lang || this.lastLoadedLocale === lang) {
          return;
        }

        this.loadSlides(lang);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialSlides']) {
      const loadedFromInput = this.applyInitialSlides();
      if (!loadedFromInput && this.disableAutoFetch) {
        this.isLoading = false;
        this.slides = [];
        this.hasMultipleSlides = false;
      }
    }
  }

  ngAfterViewInit(): void {
    // Swiper initialization is handled after slides are loaded
  }

  /**
   * Load slides from the API
   */
  private loadSlides(locale = this.localeService.languageCode || 'en'): void {
    this.lastLoadedLocale = locale;

    this.slidesService.getSlides(locale)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.data && response.data.length > 0) {
            this.slides = response.data.map((slide) => this.transformApiSlide(slide));
            this.isApiLoaded = true;
          } else {
            // No slides from API, use fallback
            this.loadFallbackSlides();
          }
          this.isLoading = false;
          this.initializeSwiperAfterLoad();
        },
        error: () => {
          // API failed, use fallback slides
          this.loadFallbackSlides();
          this.isLoading = false;
          this.initializeSwiperAfterLoad();
        },
      });
  }

  private applyInitialSlides(): boolean {
    if (!Array.isArray(this.initialSlides) || this.initialSlides.length === 0) {
      return false;
    }

    this.slides = this.initialSlides.map((slide) => this.transformApiSlide(slide));
    this.isApiLoaded = true;
    this.isLoading = false;
    this.lastLoadedLocale = this.localeService.languageCode || 'en';
    this.initializeSwiperAfterLoad();

    return true;
  }

  /**
   * Transform API slide to MainSlide format
   */
  private transformApiSlide(slide: HomepageSlide): MainSlide {
    return {
      id: slide.id,
      type: slide.type,
      duration: slide.duration,
      media_url: slide.media_url,
      poster_url: slide.poster_url,
      src: slide.media_url || undefined,
      poster: slide.poster_url || undefined,
      sources: slide.type === 'video' && slide.media_url
        ? [{ src: slide.media_url, type: 'video/mp4' }]
        : undefined,
      title: slide.title,
      subtitle: slide.subtitle,
      description: slide.description,
      button: slide.button,
      buttonText: slide.button?.text || undefined,
      buttonImage: slide.button?.image_url || undefined,
      buttonLink: slide.button?.link || undefined,
      buttonAlt: slide.button?.alt || undefined,
      buttonTarget: slide.button?.target || '_self',
    };
  }

  /**
   * Load fallback slides when API is unavailable
   */
  private loadFallbackSlides(): void {
    this.slides = [...FALLBACK_SLIDES];
    this.isApiLoaded = false;
  }

  /**
   * Initialize Swiper after slides are loaded
   */
  private initializeSwiperAfterLoad(): void {
    this.hasMultipleSlides = this.slides.length > 1;

    if (this.isBrowser && typeof Swiper !== 'undefined' && this.slides.length > 0) {
      // Small delay to ensure DOM is fully rendered
      setTimeout(() => {
        this.initializeSwiper();
      }, 100);
    }
  }

  /**
   * Initialize Swiper slider
   */
  private initializeSwiper(): void {
    // Destroy existing instance if any
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }

    const hasMultipleSlides = this.hasMultipleSlides;

    this.swiperInstance = new Swiper('.home5-banner-slider', {
      slidesPerView: 1,
      speed: 1500, // Transition speed: 1.5 seconds
      spaceBetween: 25,
      centerSlides: true,
      loop: hasMultipleSlides,
      effect: 'fade', // Fade transition effect
      allowTouchMove: hasMultipleSlides,
      watchOverflow: false,
      fadeEffect: {
        crossFade: true,
      },
      autoplay: hasMultipleSlides
        ? {
            delay: this.slides[0]?.duration || 5000, // Use first slide's duration
            disableOnInteraction: false, // Continues after user interaction
          }
        : false,
      navigation: {
        nextEl: '.home5-banner-slider-next',
        prevEl: '.home5-banner-slider-prev',
      },
      pagination: {
        el: '.paginations1',
        clickable: hasMultipleSlides,
      },
      rtl: this.localeService.dir === 'rtl', // RTL support for Arabic
    });

    // Update autoplay delay dynamically based on current slide's duration
    this.swiperInstance.on('slideChange', () => {
      if (!hasMultipleSlides || !this.swiperInstance?.autoplay) {
        return;
      }

      const currentSlide = this.slides[this.swiperInstance.realIndex];
      if (currentSlide && currentSlide.duration) {
        this.swiperInstance.params.autoplay.delay = currentSlide.duration;
        // Restart autoplay with new delay
        this.swiperInstance.autoplay.stop();
        this.swiperInstance.autoplay.start();
      }
    });
  }

  /**
   * Get the media source for a slide
   */
  getSlideMediaSrc(slide: MainSlide): string | undefined {
    return slide.media_url || slide.src;
  }

  /**
   * Get the poster image for a video slide
   */
  getSlidePoster(slide: MainSlide): string | undefined {
    return slide.poster_url || slide.poster;
  }

  /**
   * Get the button link for a slide
   */
  getButtonLink(slide: MainSlide): string | null {
    return slide.button?.link || slide.buttonLink || null;
  }

  /**
   * Get the button target for a slide
   */
  getButtonTarget(slide: MainSlide): string {
    return slide.button?.target || slide.buttonTarget || '_self';
  }

  /**
   * Get the button text for a slide
   */
  getButtonText(slide: MainSlide): string | null {
    return slide.button?.text || slide.buttonText || null;
  }

  /**
   * Get the button image URL for a slide
   */
  getButtonImage(slide: MainSlide): string | null {
    return slide.button?.image_url || slide.buttonImage || null;
  }

  /**
   * Get the button alt text for a slide
   */
  getButtonAlt(slide: MainSlide): string | null {
    return slide.button?.alt || slide.buttonAlt || null;
  }

  /**
   * Determine if the link should be handled by the Angular router.
   */
  shouldUseRouterLink(slide: MainSlide): boolean {
    const link = this.getButtonLink(slide);
    if (!link) return false;
    return this.getButtonTarget(slide) !== '_blank' && !this.isExternalLink(link);
  }

  /**
   * Check if a link is external or uses a non-router scheme.
   */
  isExternalLink(link: string): boolean {
    return /^(https?:)?\/\//i.test(link) || /^mailto:/i.test(link) || /^tel:/i.test(link);
  }

  /**
   * Check if slide content should use translation pipe (fallback slides)
   */
  isTranslationKey(value: string | null): boolean {
    if (!value) return false;
    // Fallback slides use translation keys like 'buyer_homepage.xxx'
    return value.includes('buyer_homepage.') || value.includes('.');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    // Destroy Swiper instance to prevent memory leaks
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
  }
}
